/**
 * Apple Ads install attribution, client half (v1.5.0, docs/v150-spec.md §3.8).
 *
 * The native `AdAttribution` plugin (ios/App/App/AdAttributionPlugin.swift)
 * returns an AdServices token — synchronously, off the main thread, and it
 * always resolves: { token } or { token: null, errorCode, error }, with
 * error 'simulator' in the Simulator. The token (24 h TTL) is POSTed to
 * /subscription/attribution, and the server exchanges it with Apple once per
 * account (first write wins).
 *
 *   primeAttributionToken()  first launch, signed out: mint a token and keep
 *                            { token, generatedAt } in 'bt_attr_token'
 *   captureAttribution()     once signed in: reuse the kept token while it is
 *                            under 23 h old, else mint a fresh one, and post it.
 *                            'recorded' or 'invalid' ends it for this user;
 *                            'pending' or no answer retries on a later launch —
 *                            at most 5 attempts, at least an hour apart
 *                            ('bt_attribution:<sub>' = { status, attempts, lastAttemptAt }).
 *
 * Never on the web, never in the Simulator, only when navigator.onLine, and
 * never in the way of anything the user is doing: silentFetch, no toasts, and
 * no function here ever rejects.
 *
 * Thenable-proxy rule: the registerPlugin() handle is kept in a plain wrapper
 * (`{ api }`) and only its method is ever awaited.
 */

import { registerPlugin } from '@capacitor/core';
import { silentFetch } from '../api/client';
import { getCachedAuthUser, isOffline, withTimeout } from '../../services/network';
import { isIOS, isNative } from '../utils/platform';

export const ATTR_TOKEN_KEY = 'bt_attr_token';
export const ATTR_STATE_PREFIX = 'bt_attribution:';
export const TOKEN_MAX_AGE_MS = 23 * 60 * 60 * 1000;
export const MAX_ATTEMPTS = 5;
export const MIN_ATTEMPT_GAP_MS = 60 * 60 * 1000;

const TOKEN_TIMEOUT_MS = 10000;

let wrapper = null; // { api } — plain object around the plugin proxy

function getApi() {
  if (!isNative() || !isIOS()) return null;
  if (!wrapper) {
    try {
      wrapper = { api: registerPlugin('AdAttribution') };
    } catch {
      return null;
    }
  }
  return wrapper.api;
}

/** Ask the plugin for a token: { token } or { token: null, error }. */
async function mintToken() {
  const api = getApi();
  if (!api) return { token: null, error: 'unsupported' };
  try {
    const res = await withTimeout(api.getAttributionToken(), TOKEN_TIMEOUT_MS, 'Attribution token timed out');
    const token = typeof res?.token === 'string' && res.token ? res.token : null;
    return token ? { token } : { token: null, error: res?.error || 'no-token' };
  } catch {
    return { token: null, error: 'plugin' };
  }
}

function readToken() {
  try {
    const parsed = JSON.parse(localStorage.getItem(ATTR_TOKEN_KEY) || 'null');
    if (parsed && typeof parsed.token === 'string' && parsed.token && Number.isFinite(parsed.generatedAt)) {
      return parsed;
    }
  } catch {
    /* unreadable — mint a new one */
  }
  return null;
}

function writeToken(token, generatedAt) {
  try {
    localStorage.setItem(ATTR_TOKEN_KEY, JSON.stringify({ token, generatedAt }));
  } catch {
    /* storage blocked — the next capture mints its own */
  }
}

function clearToken() {
  try {
    localStorage.removeItem(ATTR_TOKEN_KEY);
  } catch {
    /* nothing to clear */
  }
}

const isFresh = (stashed, now) =>
  !!stashed && now >= stashed.generatedAt && now - stashed.generatedAt < TOKEN_MAX_AGE_MS;

function readState(sub) {
  try {
    const parsed = JSON.parse(localStorage.getItem(`${ATTR_STATE_PREFIX}${sub}`) || 'null');
    if (parsed && typeof parsed === 'object') {
      return {
        status: typeof parsed.status === 'string' ? parsed.status : null,
        attempts: Number(parsed.attempts) || 0,
        lastAttemptAt: Number(parsed.lastAttemptAt) || 0,
      };
    }
  } catch {
    /* unreadable — start again */
  }
  return { status: null, attempts: 0, lastAttemptAt: 0 };
}

function writeState(sub, state) {
  try {
    localStorage.setItem(`${ATTR_STATE_PREFIX}${sub}`, JSON.stringify(state));
  } catch {
    /* storage blocked — worst case it tries again next launch */
  }
}

async function getAppVersion() {
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    return info?.version || '';
  } catch {
    return '';
  }
}

/**
 * First launch, signed out: mint a token and keep it, so the exchange at
 * sign-up uses one generated close to the install. Keeps an existing token
 * while it is under 23 h old.
 */
export async function primeAttributionToken() {
  try {
    if (!getApi() || isOffline()) return;
    if (isFresh(readToken(), Date.now())) return;
    const { token } = await mintToken();
    if (token) writeToken(token, Date.now());
  } catch {
    /* never reject */
  }
}

// Statuses after which this user is never tried again.
const FINAL = new Set(['recorded', 'invalid', 'unsupported']);

async function runCapture() {
  if (!getApi() || isOffline()) return;
  const sub = getCachedAuthUser()?.sub;
  if (!sub) return;
  const state = readState(sub);
  if (FINAL.has(state.status) || state.attempts >= MAX_ATTEMPTS) return;
  const now = Date.now();
  if (state.lastAttemptAt && now - state.lastAttemptAt < MIN_ATTEMPT_GAP_MS) return;

  let stashed = readToken();
  if (!isFresh(stashed, now)) {
    const minted = await mintToken();
    if (minted.error === 'simulator') {
      // The Simulator never produces a token: stop asking.
      writeState(sub, { status: 'unsupported', attempts: state.attempts, lastAttemptAt: now });
      return;
    }
    if (!minted.token) {
      writeState(sub, { status: 'pending', attempts: state.attempts + 1, lastAttemptAt: now });
      return;
    }
    stashed = { token: minted.token, generatedAt: Date.now() };
    writeToken(stashed.token, stashed.generatedAt);
  }

  const appVersion = await getAppVersion();
  const res = await silentFetch('/subscription/attribution', 'POST', {
    token: stashed.token,
    tokenGeneratedAt: new Date(stashed.generatedAt).toISOString(),
    ...(appVersion ? { appVersion } : {}),
  });
  const status = res?.status === 'recorded' || res?.status === 'invalid' ? res.status : 'pending';
  writeState(sub, { status, attempts: state.attempts + 1, lastAttemptAt: now });
  // A recorded token has done its job and an invalid one never will.
  if (status !== 'pending') clearToken();
}

let capturing = null; // one capture at a time

/**
 * Exchange the attribution token for the signed-in user. Safe to call on
 * every launch and after every sign-in: it does its own once-per-user
 * bookkeeping. Resolves when done; never rejects.
 */
export function captureAttribution() {
  if (!capturing) {
    capturing = runCapture()
      .catch(() => {})
      .finally(() => {
        capturing = null;
      });
  }
  return capturing;
}
